/**
 * IOM USA Formula Configuration
 * 
 * Defines nutrient categorization and evaluation rules for IOM USA formula.
 * This config is used by the traffic light evaluation system to determine
 * goal alignment with nutritional guidelines.
 */

export type TrafficLightStatus = 'red' | 'amber' | 'green' | 'purple' | 'grey';

export type NutrientCategory = 'energy-based' | 'absolute' | 'max-only';

export interface NutrientConfig {
  category: NutrientCategory;
  nutrientId: string; // Backend nutrient ID (e.g., 'protein', 'total_fat', 'dietary_fibre')
  displayName: string;
  caloriesPerGram: number; // For energy-based nutrients: 4 for protein/carbs, 9 for fats
}

/**
 * IOM USA Formula Configuration
 * Maps nutrients to their evaluation category and properties
 */
export const IOM_USA_FORMULA_CONFIG: Record<string, NutrientConfig> = {
  protein: {
    category: 'energy-based',
    nutrientId: 'protein',
    displayName: 'Protein',
    caloriesPerGram: 4,
  },
  carbohydrate: {
    category: 'energy-based',
    nutrientId: 'carbohydrate',
    displayName: 'Carbohydrate',
    caloriesPerGram: 4,
  },
  carbohydrates: {
    category: 'energy-based',
    nutrientId: 'carbohydrate',
    displayName: 'Carbohydrate',
    caloriesPerGram: 4,
  },
  totalFat: {
    category: 'energy-based',
    nutrientId: 'total_fat',
    displayName: 'Total Fat',
    caloriesPerGram: 9,
  },
  total_fat: {
    category: 'energy-based',
    nutrientId: 'total_fat',
    displayName: 'Total Fat',
    caloriesPerGram: 9,
  },
  saturatedFat: {
    category: 'energy-based', // Energy-based by default; treated as max-only when percentage shows "≤"
    nutrientId: 'saturated_fat',
    displayName: 'Saturated Fat',
    caloriesPerGram: 9,
  },
  saturated_fat: {
    category: 'energy-based', // Energy-based by default; treated as max-only when percentage shows "≤"
    nutrientId: 'saturated_fat',
    displayName: 'Saturated Fat',
    caloriesPerGram: 9,
  },
  freeSugars: {
    category: 'energy-based', // Energy-based by default; treated as max-only when percentage shows "≤"
    nutrientId: 'free_sugars',
    displayName: 'Free Sugars',
    caloriesPerGram: 4,
  },
  free_sugars: {
    category: 'energy-based', // Energy-based by default; treated as max-only when percentage shows "≤"
    nutrientId: 'free_sugars',
    displayName: 'Free Sugars',
    caloriesPerGram: 4,
  },
  dietaryFibre: {
    category: 'absolute',
    nutrientId: 'dietary_fibre',
    displayName: 'Fiber',
    caloriesPerGram: 0, // Not applicable
  },
  dietary_fibre: {
    category: 'absolute',
    nutrientId: 'dietary_fibre',
    displayName: 'Fiber',
    caloriesPerGram: 0,
  },
  fiber: {
    category: 'absolute',
    nutrientId: 'dietary_fibre',
    displayName: 'Fiber',
    caloriesPerGram: 0,
  },
  water: {
    category: 'absolute',
    nutrientId: 'water',
    displayName: 'Hydration',
    caloriesPerGram: 0,
  },
  transFat: {
    category: 'max-only',
    nutrientId: 'trans_fat',
    displayName: 'Trans Fat',
    caloriesPerGram: 9,
  },
  trans_fat: {
    category: 'max-only',
    nutrientId: 'trans_fat',
    displayName: 'Trans Fat',
    caloriesPerGram: 9,
  },
};

/**
 * Check if a formula identifier indicates IOM USA formula
 * 
 * Backend returns lowercase with underscores: "iom_usa"
 * Also supports legacy formats: "IOM", "IOM_US"
 */
export function isIomUsaFormula(formula: string | undefined | null): boolean {
  if (!formula) return false;
  const normalized = formula.toLowerCase().trim();
  return normalized === 'iom_usa' || normalized === 'iom' || normalized === 'iom_us';
}

/**
 * Check if a formula identifier indicates EFSA EU formula
 *
 * Backend returns lowercase with underscores: "efsa_eu"
 * Also supports: "efsa"
 */
export function isEfsaEuFormula(formula: string | undefined | null): boolean {
  if (!formula) return false;
  const normalized = formula.toLowerCase().trim();
  return normalized === 'efsa_eu' || normalized === 'efsa';
}

/**
 * Check if a formula identifier indicates SACN formula
 *
 * Backend returns lowercase with underscores: "uk_sacn"
 * Also supports: "sacn"
 */
export function isSacnFormula(formula: string | undefined | null): boolean {
  if (!formula) return false;
  const normalized = formula.toLowerCase().trim();
  return normalized === 'uk_sacn' || normalized === 'sacn';
}

/**
 * Check if a formula identifier indicates ICMR India formula
 *
 * Backend returns lowercase with underscores: "icmr_india"
 * Also supports: "icmr"
 */
export function isIcmrIndiaFormula(formula: string | undefined | null): boolean {
  if (!formula) return false;
  const normalized = formula.toLowerCase().trim();
  return normalized === 'icmr_india' || normalized === 'icmr';
}

/**
 * Check if a formula identifier indicates AUS/NZ NRV formula
 *
 * Backend returns lowercase with underscores: "ausnz_nrv"
 * Also supports: "aus_nz", "nrv"
 */
export function isAusNzNrvFormula(formula: string | undefined | null): boolean {
  if (!formula) return false;
  const normalized = formula.toLowerCase().trim();
  return normalized === 'ausnz_nrv' || normalized === 'aus_nz' || normalized === 'nrv';
}

/**
 * Check if a formula identifier indicates Nordic NNR formula
 *
 * Backend returns lowercase with underscores: "nordic_nnr"
 * Also supports: "nnr"
 */
export function isNordicNnrFormula(formula: string | undefined | null): boolean {
  if (!formula) return false;
  const normalized = formula.toLowerCase().trim();
  return normalized === 'nordic_nnr' || normalized === 'nnr';
}

/**
 * Get nutrient configuration for a given nutrient key
 */
export function getNutrientConfig(nutrientKey: string): NutrientConfig | null {
  return IOM_USA_FORMULA_CONFIG[nutrientKey] || null;
}

/**
 * Determine if a nutrient should be treated as max-only based on percentage display
 * (e.g., "≤11%" indicates max-only evaluation)
 */
export function isMaxOnlyByPercentage(percentageRange: string | null | undefined): boolean {
  if (!percentageRange) return false;
  return percentageRange.trim().startsWith('≤');
}
